// Small helper library for detecting/rendering hero + gallery media (image / MP4 / YouTube / Facebook).

const YOUTUBE_RE =
  /(?:youtube\.com\/(?:.*[?&]v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{6,15})/i;

const FACEBOOK_RE =
  /^https?:\/\/(?:www\.|web\.|m\.)?(?:facebook|fb)\.com\/(?:reel\/|watch\/?\?v=|[^\/]+\/videos\/|video\.php|share\/[rvp]\/)/i;

const DIRECT_VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;
const KNOWN_IMAGE_RE = /\.(jpe?g|png|gif|webp|svg|bmp|avif|heic|heif)(\?|#|$)/i;

export function detectVideoKind(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (trimmed.startsWith("data:video/")) return "mp4";
  if (KNOWN_IMAGE_RE.test(trimmed)) return null;
  if (YOUTUBE_RE.test(trimmed)) return "youtube";
  if (FACEBOOK_RE.test(trimmed)) return "facebook";
  if (DIRECT_VIDEO_RE.test(trimmed)) return "mp4";
  // Fallback: unknown video URL — treat as direct MP4 if it looks like a plain http(s) URL
  // (covers CDNs that serve MP4s without a .mp4 extension — Cloudflare Stream, Bunny.net, etc.)
  if (/^https?:\/\//i.test(trimmed)) return "mp4";
  return null;
}

export function youtubeId(url) {
  if (!url) return null;
  const m = String(url).match(YOUTUBE_RE);
  return m ? m[1] : null;
}

export function youtubeEmbedUrl(url, { background = false, controls = true } = {}) {
  const id = youtubeId(url);
  if (!id) return "";
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    modestbranding: "1",
    rel: "0",
  });
  if (background) {
    params.set("loop", "1");
    params.set("playlist", id);
    params.set("controls", "0");
    params.set("showinfo", "0");
    params.set("iv_load_policy", "3");
  } else if (!controls) {
    params.set("controls", "0");
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

// Facebook video plugin URL — works for public videos + many reels.
// Facebook decides at render-time whether to embed or block; when it blocks
// we surface a click-through card (see FacebookVideoEmbed component).
export function facebookEmbedUrl(url, { width = 720 } = {}) {
  if (!url) return "";
  const params = new URLSearchParams({
    href: url,
    show_text: "false",
    width: String(width),
    height: String(Math.round(width * 1.5)),
    autoplay: "true",
  });
  return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
}

// Poster (still image) for a media item — used for video thumbnails and lightbox fallback
export function posterFor(item) {
  if (!item) return "";
  return item.image_data || item.image_url || "";
}

// Return the primary display media of a gallery/hero item:
//   { kind: 'video'|'image'|'none', src, poster, videoKind }
export function primaryMedia(item) {
  if (!item) return { kind: "none" };
  const videoKind = detectVideoKind(item.video_url);
  if (videoKind) {
    return {
      kind: "video",
      videoKind, // 'mp4' | 'youtube'
      src: item.video_url,
      poster: posterFor(item),
    };
  }
  const img = item.image_data || item.image_url;
  if (img) return { kind: "image", src: img };
  return { kind: "none" };
}
